/**
 * 灯光设置协议（command = 5，byte3：1 = 读取全部，2 = 写入全部，
 * 4 = 单独设置灯效，5 = 切换灯效）。
 *
 * 与仓库其它命令一致：协议表里的字段编号是从完整包 byte4 起算的 payload 偏移，
 * 因此完整包下标统一加 4。
 *
 *   payload byte0-1   uint16_t effectSleepTime   灯效休眠时间（秒）
 *                                                0 = 设备休眠前不关灯效；1…n = 到时关灯效
 *   payload byte2     uint8_t  effect            0 常亮 / 1 霓虹 / 2 呼吸 / 3 关闭
 *   payload byte3-86  effect_info_t effectInfo[3]
 *                    固定索引：0 常亮 / 1 霓虹 / 2 呼吸
 *
 * effect_info_t（28 字节）：
 *   0      type              设备侧常回读成 0/0/0，固件按数组索引定位灯效，
 *                            不以此字段为准；解析保留为 rawType 仅供排查，
 *                            回写时逐字节原样带回，任何路径都不改写它
 *   1      color             0-6 固定色（红蓝绿紫青黄白）/ 7 自定义色 / 0xff RGB 模式
 *   2      speed             0-4，索引 speedTable
 *   3      brightness        0-5，索引 brightnessTable
 *   4      direction         0-n
 *   5      fade              0-2
 *   6-8    customerRGB[3]    自定义颜色
 *   9-11   backgroundRGB[3]  自定义背景色（NC）
 *   12-17  brightnessTable[6]  各亮度档的实际值 0-255
 *   18-27  speedTable[5][2]    各速度档的实际周期 ms（uint16，1000-15000）
 *
 * payload 最长 3 + 28 × 3 = 87 字节，超过单个 64 字节报告能带的 59 字节，
 * 因此和宏数据一样分包收发：
 *   byte1 = 包索引，byte2 = 本包 payload 长度（最后一包置 bit7），
 *   byte4-62 = 本包 payload（59 字节）。
 * 读到最后一包后按索引拼回完整 payload 再解析。
 *
 * 「写入全部」按读回的原 payload 整体回写：只改界面上动过的字段，未使用的
 * effectInfo 组、每组的 type、以及设备只读的档位表都原样带回，避免把设备侧
 * 数据清零或改错。改哪一组只由 state.effect 的索引决定。
 *
 * 「单独设置灯效」（byte3 = 4）只下发一个灯效，payload 共 29 字节，单包装得下：
 *   payload byte0     uint8_t effect 序号   0 常亮 / 1 霓虹 / 2 呼吸
 *   payload byte1-28  effect_info_t         该灯效的参数
 * 界面上只改当前灯效自己的参数（颜色、亮度、速度…）时走这一条。
 *
 * 「切换灯效」（byte3 = 5）只换当前灯效，payload 2 字节：
 *   payload byte0（完整包 byte4）  uint8_t effect 序号
 *                                  0 常亮 / 1 霓虹 / 2 呼吸 / 3 关闭
 *   payload byte1（完整包 byte5）  保留，固定 0
 * 有效数据只有 effect 一个字节，但设备要求长度字段按 2 计（byte2 = 0x80 | 2），
 * 按 1 下发时设备不认这一包。
 * 设备拿 effect 字节替换掉已存数据里的 effect，其余字节（各组 effect_info_t、
 * effectSleepTime、档位表）一概不动。所以切换灯光模式走这一条，不再整包回写：
 * 整包回写会把界面上的当前值覆盖到设备那一组参数里，等于切模式时顺手改了数据。
 * 只有改 effectSleepTime 才需要「写入全部」。
 */

import { parseCommandByte } from './ProtocolHeader';

export const LIGHTING_COMMAND = 0x05;
/** byte3：1 = 读取全部，2 = 写入全部，4 = 单独设置灯效，5 = 切换灯效 */
export const LIGHTING_READ_TYPE = 0x01;
export const LIGHTING_WRITE_TYPE = 0x02;
export const LIGHTING_EFFECT_WRITE_TYPE = 0x04;
export const LIGHTING_EFFECT_SWITCH_TYPE = 0x05;

/**
 * byte3=5 的 payload：effect 序号 + 1 字节保留。
 * 有效数据只有 effect，但设备要求长度字段按 2 计（byte2 = 0x80 | 2）。
 */
export const LIGHTING_EFFECT_SWITCH_PAYLOAD_SIZE = 2;

/** 完整包偏移（= payload 偏移 + 4） */
export const EFFECT_SLEEP_TIME_OFFSET = 4;
export const EFFECT_OFFSET = 6;
export const EFFECT_INFO_OFFSET = 7;
export const EFFECT_INFO_SIZE = 28;
export const MAX_EFFECT_INFO = 3;

/** 包头长度（byte0-3）与每包能带的 payload 字节数（byte4-62），与宏一致 */
export const LIGHTING_HEADER_SIZE = 4;
export const LIGHTING_PACKET_PAYLOAD_SIZE = 59;
/** 完整 payload：effectSleepTime(2) + effect(1) + effectInfo[3] */
export const LIGHTING_PAYLOAD_SIZE = 3 + EFFECT_INFO_SIZE * MAX_EFFECT_INFO;
/** 完整 payload 需要的包数 */
export const LIGHTING_MAX_PACKETS = Math.ceil(
  LIGHTING_PAYLOAD_SIZE / LIGHTING_PACKET_PAYLOAD_SIZE
);

/** effect_info_t 内部偏移 */
const INFO = Object.freeze({
  type: 0,
  color: 1,
  speed: 2,
  brightness: 3,
  direction: 4,
  fade: 5,
  customerRGB: 6,
  backgroundRGB: 9,
  brightnessTable: 12,
  speedTable: 18,
});

export const LIGHTING_EFFECT_STEADY = 0;
export const LIGHTING_EFFECT_NEON = 1;
export const LIGHTING_EFFECT_BREATH = 2;
export const LIGHTING_EFFECT_OFF = 3;

/**
 * fields 只影响界面上哪些控件可用，不影响下发的字节。
 */
export const LIGHTING_EFFECTS = Object.freeze([
  Object.freeze({
    value: LIGHTING_EFFECT_STEADY,
    label: '常亮',
    hint: '固定单色常亮',
    fields: Object.freeze(['color', 'brightness']),
  }),
  Object.freeze({
    value: LIGHTING_EFFECT_NEON,
    label: '霓虹',
    hint: '多色循环流动',
    fields: Object.freeze(['brightness', 'speed', 'direction', 'fade']),
  }),
  Object.freeze({
    value: LIGHTING_EFFECT_BREATH,
    label: '呼吸',
    hint: '单色明暗渐变',
    fields: Object.freeze(['color', 'brightness', 'speed', 'fade']),
  }),
  Object.freeze({
    value: LIGHTING_EFFECT_OFF,
    label: '关闭',
    hint: '熄灭所有灯光',
    fields: Object.freeze([]),
  }),
]);

export const LIGHTING_COLOR_CUSTOM = 7;
export const LIGHTING_COLOR_RGB_MODE = 0xff;

/** color 0-6 是固定色，顺序取自协议：红、蓝、绿、紫、青、黄、白。 */
export const LIGHTING_COLORS = Object.freeze([
  Object.freeze({ value: 0, label: '红', css: '#ff3b30' }),
  Object.freeze({ value: 1, label: '蓝', css: '#007aff' }),
  Object.freeze({ value: 2, label: '绿', css: '#34c759' }),
  Object.freeze({ value: 3, label: '紫', css: '#af52de' }),
  Object.freeze({ value: 4, label: '青', css: '#32ade6' }),
  Object.freeze({ value: 5, label: '黄', css: '#ffcc00' }),
  Object.freeze({ value: 6, label: '白', css: '#f2f2f7' }),
]);

export const LIGHTING_DIRECTIONS = Object.freeze([
  Object.freeze({ value: 0, label: '正向' }),
  Object.freeze({ value: 1, label: '反向' }),
]);

export const LIGHTING_FADE_OPTIONS = Object.freeze([
  Object.freeze({ value: 0, label: '关闭' }),
  Object.freeze({ value: 1, label: '淡入' }),
  Object.freeze({ value: 2, label: '淡出' }),
]);

export const LIGHTING_SPEED_MIN = 0;
export const LIGHTING_SPEED_MAX = 4;
export const LIGHTING_BRIGHTNESS_MIN = 0;
export const LIGHTING_BRIGHTNESS_MAX = 5;
export const LIGHTING_SLEEP_TIME_MIN = 0;
export const LIGHTING_SLEEP_TIME_MAX = 0xffff;

const clampInt = (value, min, max) => {
  const numeric = Math.round(Number(value));
  if (!Number.isFinite(numeric)) return min;
  return Math.max(min, Math.min(max, numeric));
};

export const clampSpeed = (value) =>
  clampInt(value, LIGHTING_SPEED_MIN, LIGHTING_SPEED_MAX);
export const clampBrightness = (value) =>
  clampInt(value, LIGHTING_BRIGHTNESS_MIN, LIGHTING_BRIGHTNESS_MAX);
export const clampFade = (value) => clampInt(value, 0, 2);
export const clampSleepTime = (value) =>
  clampInt(value, LIGHTING_SLEEP_TIME_MIN, LIGHTING_SLEEP_TIME_MAX);

export const DEFAULT_LIGHTING_STATE = Object.freeze({
  effectSleepTime: 0,
  effect: LIGHTING_EFFECT_STEADY,
  color: 0,
  speed: 2,
  brightness: 5,
  direction: 0,
  fade: 0,
  customerRGB: Object.freeze([255, 255, 255]),
});

/** 参数在界面上的固定展示顺序与名称 */
export const LIGHTING_FIELD_ORDER = Object.freeze([
  'color',
  'brightness',
  'speed',
  'direction',
  'fade',
]);

export const LIGHTING_FIELD_LABELS = Object.freeze({
  color: '颜色',
  brightness: '亮度',
  speed: '速度',
  direction: '方向',
  fade: '淡入淡出',
});

export function getLightingEffect(value) {
  return (
    LIGHTING_EFFECTS.find((effect) => effect.value === Number(value)) || null
  );
}

/**
 * 某个灯效需要设置的字段，按固定顺序返回。
 * 界面只渲染这些字段，其余参数不出现。
 */
export function getEffectFields(effectValue) {
  const effect = getLightingEffect(effectValue);
  if (!effect) return [];
  return LIGHTING_FIELD_ORDER.filter((field) => effect.fields.includes(field));
}

/** 灯效需要设置的字段中文名，用于在模式卡片上提示。 */
export function getEffectFieldLabels(effectValue) {
  return getEffectFields(effectValue).map((field) => LIGHTING_FIELD_LABELS[field]);
}

export function getLightingColor(value) {
  return LIGHTING_COLORS.find((color) => color.value === Number(value)) || null;
}

export function rgbToHex([r = 0, g = 0, b = 0] = []) {
  return `#${[r, g, b]
    .map((value) => clampInt(value, 0, 255).toString(16).padStart(2, '0'))
    .join('')}`;
}

export function hexToRgb(hex) {
  const match = /^#?([\da-f]{6})$/i.exec(String(hex || ''));
  if (!match) return [0, 0, 0];
  const value = parseInt(match[1], 16);
  return [(value >> 16) & 0xff, (value >> 8) & 0xff, value & 0xff];
}

function readUint16LE(data, offset) {
  return (data[offset] | (data[offset + 1] << 8)) >>> 0;
}

/**
 * 解析一组 effect_info_t。
 * @param {Uint8Array} data 完整包
 * @param {number} offset 该组在完整包中的起始下标
 */
function parseEffectInfo(data, offset, index) {
  if (offset + EFFECT_INFO_SIZE > data.length) return null;
  const at = (field) => offset + INFO[field];
  const speedTable = [];
  for (let i = 0; i < 5; i++) {
    speedTable.push(readUint16LE(data, at('speedTable') + i * 2));
  }
  return {
    // effectInfo 是固定索引数组。保留设备原始 type 便于排查，但界面和写入
    // 始终以数组索引为准，避免设备回读 0/0/0 时把呼吸参数误当成第 0 组。
    type: index,
    rawType: data[at('type')],
    color: data[at('color')],
    speed: data[at('speed')],
    brightness: data[at('brightness')],
    direction: data[at('direction')],
    fade: data[at('fade')],
    customerRGB: Array.from(
      data.slice(at('customerRGB'), at('customerRGB') + 3)
    ),
    backgroundRGB: Array.from(
      data.slice(at('backgroundRGB'), at('backgroundRGB') + 3)
    ),
    brightnessTable: Array.from(
      data.slice(at('brightnessTable'), at('brightnessTable') + 6)
    ),
    speedTable,
  };
}

/**
 * 解析 command=5 灯光包。effectInfo 按包内实际长度解析，缺的组直接略过。
 * @param {Uint8Array} data
 */
export function parseLightingData(data) {
  if (!data || data.length < EFFECT_INFO_OFFSET) return null;

  // byte2 低 7 位声明的是 payload 长度：单包响应里 byte4 之后可能还有填充字节，
  // 只按缓冲长度切分会多解析出一组全零的 effectInfo。
  const declared = data[2] & 0x7f;
  const available = data.length - LIGHTING_HEADER_SIZE;
  const payloadLength =
    declared > 0 && declared <= available ? declared : available;
  const groupCount = Math.min(
    MAX_EFFECT_INFO,
    Math.max(0, Math.floor((payloadLength - 3) / EFFECT_INFO_SIZE))
  );

  const effectInfos = [];
  for (let i = 0; i < groupCount; i++) {
    const info = parseEffectInfo(
      data,
      EFFECT_INFO_OFFSET + i * EFFECT_INFO_SIZE,
      i
    );
    if (!info) break;
    effectInfos.push(info);
  }

  return {
    command: parseCommandByte(data[0]).command,
    index: data[1],
    length: data[2],
    type: data[3],
    effectSleepTime: readUint16LE(data, EFFECT_SLEEP_TIME_OFFSET),
    effect: data[EFFECT_OFFSET],
    effectInfos,
  };
}

/**
 * 从解析结果里取出界面用的灯光状态。
 * effectInfo 是固定索引数组，直接用当前 effect 取对应组。
 */
export function readLightingState(parsed) {
  if (!parsed || parsed.effect === undefined) return null;
  const effect = Number(parsed.effect) || 0;
  const infos = parsed.effectInfos || [];
  const info =
    effect >= 0 && effect < MAX_EFFECT_INFO ? infos[effect] || null : null;

  return {
    effectSleepTime: clampSleepTime(parsed.effectSleepTime),
    effect,
    color: info ? Number(info.color) : DEFAULT_LIGHTING_STATE.color,
    speed: clampSpeed(info?.speed ?? DEFAULT_LIGHTING_STATE.speed),
    brightness: clampBrightness(
      info?.brightness ?? DEFAULT_LIGHTING_STATE.brightness
    ),
    direction: Number(info?.direction) || 0,
    fade: clampFade(info?.fade ?? 0),
    customerRGB: info?.customerRGB || [...DEFAULT_LIGHTING_STATE.customerRGB],
    brightnessTable: info?.brightnessTable || null,
    speedTable: info?.speedTable || null,
  };
}

/** byte2 的 bit7 = 本次数据到此为止 */
export function isLastLightingPacket(packet) {
  return Boolean(packet?.length > 2 && (packet[2] & 0x80));
}

/**
 * 取出单个响应包里的 payload。
 * byte2 低 7 位是本包的 payload 长度；为 0 或越界时按满包处理。
 */
export function lightingPacketPayload(packet) {
  if (!packet?.length) return new Uint8Array(0);
  const declared = packet[2] & 0x7f;
  const available = Math.max(0, packet.length - LIGHTING_HEADER_SIZE);
  const length = Math.min(
    declared > 0 ? declared : LIGHTING_PACKET_PAYLOAD_SIZE,
    LIGHTING_PACKET_PAYLOAD_SIZE,
    available
  );
  return packet.slice(LIGHTING_HEADER_SIZE, LIGHTING_HEADER_SIZE + length);
}

/**
 * 把分包收到的响应拼成「4 字节包头 + 完整 payload」的缓冲。
 * @param {Uint8Array[]} payloads 按包索引排好的各包 payload
 * @param {Uint8Array} firstPacket 第 0 包（用于取包头）
 */
export function mergeLightingPackets(payloads, firstPacket) {
  const total = payloads.reduce((sum, chunk) => sum + chunk.length, 0);
  const merged = new Uint8Array(LIGHTING_HEADER_SIZE + total);
  if (firstPacket?.length) {
    merged.set(firstPacket.slice(0, LIGHTING_HEADER_SIZE));
  }
  merged[0] = LIGHTING_COMMAND;
  merged[1] = 0; // 索引重置
  merged[2] = 0x80 | Math.min(total, 0x7f);
  let offset = LIGHTING_HEADER_SIZE;
  payloads.forEach((chunk) => {
    merged.set(chunk, offset);
    offset += chunk.length;
  });
  return merged;
}

/** 从合并后的缓冲里取出 payload（去掉 4 字节包头）。 */
export function extractLightingPayload(merged) {
  if (!merged?.length || merged.length <= LIGHTING_HEADER_SIZE) return null;
  return merged.slice(LIGHTING_HEADER_SIZE);
}

/**
 * 找出「写入全部」时该改哪一组 effectInfo。
 * effectInfo 固定按索引映射：0 常亮 / 1 霓虹 / 2 呼吸。
 * 关闭灯效没有参数组，只修改 payload.effect。
 */
function resolveEffectInfoIndex(payload, effect) {
  const groupCount = Math.floor((payload.length - 3) / EFFECT_INFO_SIZE);
  const index = Number(effect);
  if (!Number.isInteger(index) || index < 0 || index >= groupCount) return -1;
  return index;
}

/**
 * 组装 command=5 / type=2「写入全部」的完整 payload。
 *
 * 以读回的原 payload 为基底整体回写：其它 effectInfo 组、backgroundRGB 与
 * brightnessTable / speedTable 都原样带回，只覆盖界面上可编辑的字段。
 *
 * @param {object} state 界面状态
 * @param {Uint8Array|number[]|null} basePayload 读回的完整 payload
 * @returns {Uint8Array} 待下发的 payload
 */
export function encodeLightingPayload(state, basePayload = null) {
  const size = basePayload?.length
    ? Math.min(basePayload.length, LIGHTING_PAYLOAD_SIZE)
    : LIGHTING_PAYLOAD_SIZE;
  const payload = new Uint8Array(size);
  if (basePayload?.length) {
    payload.set(basePayload.slice(0, size));
  }

  const effect = Number(state.effect) || 0;
  const sleepTime = clampSleepTime(state.effectSleepTime);
  payload[0] = sleepTime & 0xff;
  payload[1] = (sleepTime >> 8) & 0xff;
  payload[2] = effect & 0xff;

  // 固件按数组索引定位灯效参数，不看 effect_info_t.type：设备回读的 type
  // 经常是 0/0/0，却仍能正确保存对应索引的颜色。所以这里一个 type 字节都不动，
  // 三组原始 type 随 basePayload 逐字节带回；关闭灯效（effect=3）没有参数组，
  // 只改 payload[2]。
  const infoIndex = resolveEffectInfoIndex(payload, effect);
  const infoBase = 3 + infoIndex * EFFECT_INFO_SIZE;
  if (infoIndex >= 0 && infoBase + EFFECT_INFO_SIZE <= payload.length) {
    const at = (field) => infoBase + INFO[field];
    payload[at('color')] = Number(state.color) & 0xff;
    payload[at('speed')] = clampSpeed(state.speed);
    payload[at('brightness')] = clampBrightness(state.brightness);
    payload[at('direction')] = Number(state.direction) & 0xff;
    payload[at('fade')] = clampFade(state.fade);

    const [r = 0, g = 0, b = 0] = state.customerRGB || [];
    payload[at('customerRGB')] = clampInt(r, 0, 255);
    payload[at('customerRGB') + 1] = clampInt(g, 0, 255);
    payload[at('customerRGB') + 2] = clampInt(b, 0, 255);
  }

  return payload;
}

/** 「单独设置灯效」payload：effect 序号(1) + effect_info_t(28) */
export const LIGHTING_EFFECT_PAYLOAD_SIZE = 1 + EFFECT_INFO_SIZE;

/** 能用「单独设置灯效」下发的灯效序号：0 常亮 / 1 霓虹 / 2 呼吸 */
export const LIGHTING_SINGLE_WRITE_EFFECTS = Object.freeze([
  LIGHTING_EFFECT_STEADY,
  LIGHTING_EFFECT_NEON,
  LIGHTING_EFFECT_BREATH,
]);

/**
 * 该灯效能否用 byte3=4 单独下发。
 * 「关闭」没有对应的 effect_info_t，只能走「写入全部」。
 */
export function supportsSingleEffectWrite(effect) {
  return LIGHTING_SINGLE_WRITE_EFFECTS.includes(Number(effect));
}

/**
 * 组装单个 effect_info_t（28 字节）。
 *
 * 基底取读回 payload 里同一索引的那组（effectInfo 固定 0 常亮 / 1 霓虹 /
 * 2 呼吸），只覆盖界面上可编辑的字段，type、backgroundRGB 与 brightnessTable /
 * speedTable 都原样带回。灯效由 payload byte0 的序号决定，不靠 type。
 *
 * @param {object} state 界面状态
 * @param {Uint8Array|number[]|null} basePayload 读回的完整 payload（不含包头）
 * @returns {Uint8Array} 28 字节 effect_info_t
 */
export function encodeEffectInfo(state, basePayload = null) {
  const effect = Number(state.effect) || 0;
  const info = new Uint8Array(EFFECT_INFO_SIZE);

  if (basePayload?.length) {
    const index = resolveEffectInfoIndex(basePayload, effect);
    const start = 3 + index * EFFECT_INFO_SIZE;
    if (index >= 0 && start + EFFECT_INFO_SIZE <= basePayload.length) {
      info.set(basePayload.slice(start, start + EFFECT_INFO_SIZE));
    }
  }

  // type 不动：设备原样带回什么就写回什么（无基底时保持 0）。
  info[INFO.color] = Number(state.color) & 0xff;
  info[INFO.speed] = clampSpeed(state.speed);
  info[INFO.brightness] = clampBrightness(state.brightness);
  info[INFO.direction] = Number(state.direction) & 0xff;
  info[INFO.fade] = clampFade(state.fade);

  const [r = 0, g = 0, b = 0] = state.customerRGB || [];
  info[INFO.customerRGB] = clampInt(r, 0, 255);
  info[INFO.customerRGB + 1] = clampInt(g, 0, 255);
  info[INFO.customerRGB + 2] = clampInt(b, 0, 255);

  return info;
}

/**
 * 组装 command=5 / type=4「单独设置灯效」的 payload：序号 + effect_info_t。
 *
 * @param {object} state 界面状态
 * @param {Uint8Array|number[]|null} basePayload 读回的完整 payload（不含包头）
 * @returns {Uint8Array} 29 字节 payload
 */
export function encodeEffectWritePayload(state, basePayload = null) {
  const payload = new Uint8Array(LIGHTING_EFFECT_PAYLOAD_SIZE);
  payload[0] = Number(state.effect) & 0xff;
  payload.set(encodeEffectInfo(state, basePayload), 1);
  return payload;
}

/**
 * 组装 command=5 / type=5「切换灯效」的 payload：effect 序号 + 1 字节保留。
 *
 * 设备收到后拿 effect 替换已存数据里的 effect，别的字节都不动，所以这里也不带
 * 任何 effect_info_t —— 切模式就只切模式。保留字节固定 0，它的存在只是为了让
 * 长度字段等于设备要求的 2（byte2 = 0x80 | 2）。
 *
 * @param {object|number} state 界面状态，或直接给 effect 序号
 * @returns {Uint8Array} 2 字节 payload
 */
export function encodeEffectSwitchPayload(state) {
  const effect = Number(
    typeof state === 'object' && state !== null ? state.effect : state
  );
  const payload = new Uint8Array(LIGHTING_EFFECT_SWITCH_PAYLOAD_SIZE);
  payload[0] = (Number.isFinite(effect) ? effect : 0) & 0xff;
  return payload;
}

/**
 * 把「切换灯效」的结果套到读回的完整缓冲上：只改 effect 这一个字节，
 * 后面的 effect_info_t、effectSleepTime、档位表原样保留。
 *
 * 设备对 byte3=5 的处理就是这样，本地缓存也照做，免得切完模式还要等一次
 * 整包回读才对得上。
 *
 * @param {Uint8Array|null} buffer 已入库的完整缓冲（4 字节包头 + payload）
 * @param {number} effect 新的灯效序号
 * @returns {Uint8Array|null} 改好的副本；缓冲不可用时返回 null
 */
export function applyEffectSwitchToBuffer(buffer, effect) {
  if (!buffer?.length || buffer.length <= EFFECT_OFFSET) return null;
  const next = new Uint8Array(buffer);
  next[EFFECT_OFFSET] = Number(effect) & 0xff;
  return next;
}

/**
 * 把 payload 拆成若干 64 字节包，最后一包的 byte2 置 bit7（本次到此为止）。
 * @param {Uint8Array} payload
 * @param {number} type byte3
 * @returns {Uint8Array[]}
 */
export function splitLightingPackets(payload, type = LIGHTING_WRITE_TYPE) {
  const packetCount = Math.max(
    1,
    Math.ceil(payload.length / LIGHTING_PACKET_PAYLOAD_SIZE)
  );
  const packets = [];

  for (let i = 0; i < packetCount; i++) {
    const start = i * LIGHTING_PACKET_PAYLOAD_SIZE;
    const chunk = payload.slice(start, start + LIGHTING_PACKET_PAYLOAD_SIZE);
    const packet = new Uint8Array(64);
    packet[0] = LIGHTING_COMMAND;
    packet[1] = i;
    packet[2] =
      i === packetCount - 1 ? 0x80 | chunk.length : chunk.length;
    packet[3] = type;
    packet.set(chunk, LIGHTING_HEADER_SIZE);
    packets.push(packet);
  }

  return packets;
}
